import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
  PoTableColumnLabel
} from '@po-ui/ng-components';
import { interval, Subscription, switchMap } from 'rxjs';
import { ExtractionRun, ExtractionRunFilters, ExtractionRunStatus } from '../../interfaces/extraction-run';
import { ExtractRequest } from '../../interfaces/extract-request';
import { SonarExtractorService } from '../../services/sonar-extractor.service';
import { DEFAULT_EXCLUDE_PATTERNS, formatDateTime, triggerDownload } from '../../utils/sonar-extractor.utils';

@Component({
  selector: "app-sonar-extractor-runs",
  templateUrl: "./sonar-extractor-runs.component.html",
  styleUrls: ["./sonar-extractor-runs.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SonarExtractorRunsComponent implements OnInit {
  @ViewChild("modalExtract", { static: true }) modalExtract!: PoModalComponent;
  @ViewChild("modalDetail", { static: true }) modalDetail!: PoModalComponent;

  runs: ExtractionRun[] = [];
  loading = false;
  exportingCsv = false;
  selectedRun: ExtractionRun | null = null;
  runDetailItems: { label: string; value: string }[] = [];

  // Paginação
  currentPage = 1;
  readonly runsLimit = 10;
  hasMoreRuns = false;

  // Filtros
  filters: ExtractionRunFilters = {};
  filterStatusOptions: PoSelectOption[] = [
    { label: "Todos", value: "ALL" },
    { label: "Em andamento", value: "RUNNING" },
    { label: "Sucesso", value: "SUCCESS" },
    { label: "Falhou", value: "FAILED" },
  ];
  filterPeriod: { start: string; end: string } = { start: "", end: "" };
  filterStatus: ExtractionRunStatus | "ALL" = "ALL";

  // Modal de extração
  excludeProjectsRaw = "";
  extracting = false;

  modalPrimaryAction: PoModalAction = {
    label: "Iniciar Extração",
    loading: false,
    action: () => this.confirmExtract(),
  };

  modalSecondaryAction: PoModalAction = {
    label: "Cancelar",
    action: () => this.modalExtract.close(),
  };

  modalDetailCloseAction: PoModalAction = {
    label: "Fechar",
    action: () => this.modalDetail.close(),
  };

  // Tabela
  readonly tableColumns: PoTableColumn[] = [
    { property: "id",             label: "ID",        type: "number" },
    { property: "status",         label: "Status",    type: "label", labels: this.buildStatusLabels() },
    { property: "started_at",     label: "Início",    type: "dateTime" },
    { property: "finished_at",    label: "Fim",       type: "dateTime" },
    { property: "total_projects", label: "Projetos",  type: "number" },
    { property: "total_issues",   label: "Issues",    type: "number" },
  ];

  readonly tableActions: PoTableAction[] = [
    {
      label: "Ver Detalhes",
      icon: "po-icon-eye",
      action: (run: ExtractionRun) => this.openDetailModal(run),
    },
  ];

  private pollingSubscription: Subscription | null = null;

  constructor(
    private sonarService: SonarExtractorService,
    private notification: PoNotificationService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Adia para após o primeiro ciclo de change detection do Angular,
    // evitando NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
    // causado pelo po-table tentar renderizar o loading overlay durante ngOnInit.
    setTimeout(() => this.loadRuns());
  }

  loadRuns(append = false): void {
    this.loading = true;
    const activeFilters: ExtractionRunFilters = {
      page:  this.currentPage,
      limit: this.runsLimit,
    };
    if (this.filterStatus && this.filterStatus !== 'ALL') activeFilters.status = this.filterStatus as ExtractionRunStatus;
    if (this.filterPeriod.start)   activeFilters.startDate = this.filterPeriod.start;
    if (this.filterPeriod.end)     activeFilters.endDate   = this.filterPeriod.end;

    this.sonarService.getExtractionRuns(activeFilters).subscribe({
      next: (result) => {
        this.runs = append ? [...this.runs, ...result.runs] : result.runs;
        this.hasMoreRuns = this.currentPage < result.total_pages;
        this.loading = false;
        this.cdr.markForCheck();
        this.startPollingIfNeeded();
      },
      error: () => {
        this.notification.error("Erro ao carregar as execuções de extração.");
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadMoreRuns(): void {
    this.currentPage++;
    this.loadRuns(true);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.runs = [];
    this.loadRuns();
  }

  exportCsv(): void {
    this.exportingCsv = true;
    const filters: ExtractionRunFilters = {};
    if (this.filterStatus && this.filterStatus !== 'ALL') filters.status = this.filterStatus as ExtractionRunStatus;
    if (this.filterPeriod.start) filters.startDate = this.filterPeriod.start;
    if (this.filterPeriod.end)   filters.endDate   = this.filterPeriod.end;

    this.sonarService.exportExtractionRunsCsv(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: blob => {
        triggerDownload(blob, 'extraction-runs.csv');
        this.exportingCsv = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notification.error('Erro ao exportar as execuções de extração em CSV.');
        this.exportingCsv = false;
        this.cdr.markForCheck();
      },
    });
  }

  openExtractModal(): void {
    this.excludeProjectsRaw = DEFAULT_EXCLUDE_PATTERNS;
    this.modalExtract.open();
  }

  confirmExtract(): void {
    this.extracting = true;
    this.modalPrimaryAction.loading = true;

    const request: ExtractRequest = {
      excludeProjects: this.excludeProjectsRaw
        ? this.excludeProjectsRaw.split("\n").map((p) => p.trim()).filter(Boolean)
        : undefined,
    };

    this.sonarService.postExtract(request).subscribe({
      next: (res) => {
        this.extracting = false;
        this.modalPrimaryAction.loading = false;
        this.modalExtract.close();
        this.notification.success(`Extração iniciada com sucesso. Run ID: ${res.run_id}`);
        this.loadRuns();
      },
      error: () => {
        this.extracting = false;
        this.modalPrimaryAction.loading = false;
        this.notification.error("Erro ao iniciar a extração. Verifique se já existe uma extração em andamento.");
      },
    });
  }

  openDetailModal(run: ExtractionRun): void {
    this.selectedRun = run;
    this.runDetailItems = this.buildDetailItems(run);
    this.modalDetail.open();
  }

  buildDetailItems(run: ExtractionRun): { label: string; value: string }[] {
    return [
      { label: 'ID',             value: run.id.toString() },
      { label: 'Status',         value: this.getStatusLabelText(run.status) },
      { label: 'Início',         value: formatDateTime(run.started_at) },
      { label: 'Fim',            value: formatDateTime(run.finished_at) },
      { label: 'Duração',        value: this.getDuration(run) },
      { label: 'Total Projetos', value: run.total_projects?.toString() ?? '-' },
      { label: 'Total Issues',   value: run.total_issues?.toString()   ?? '-' },
    ];
  }

  getStatusLabelText(status: ExtractionRunStatus): string {
    const map: Record<ExtractionRunStatus, string> = {
      SUCCESS: 'Sucesso',
      FAILED: 'Falhou',
      RUNNING: 'Em andamento',
    };
    return map[status] ?? status;
  }

  formatDateTime(dateStr: string): string {
    return formatDateTime(dateStr);
  }

  getDuration(run: ExtractionRun): string {
    if (!run.started_at || !run.finished_at) return '-';
    const start = new Date(run.started_at).getTime();
    const end = new Date(run.finished_at).getTime();
    const diffMs = end - start;
    if (diffMs < 0) return '-';
    const totalSec = Math.floor(diffMs / 1000);
    const hours   = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (hours > 0)   return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  private startPollingIfNeeded(): void {
    const runningRun = this.runs.find((r) => r.status === "RUNNING");
    if (!runningRun) {
      this.stopPolling();
      return;
    }
    if (this.pollingSubscription) {
      return; // já está fazendo polling
    }
    this.notification.warning(`Extração #${runningRun.id} em andamento. Atualizando automaticamente...`);

    const poll = () => {
      this.pollingSubscription = this.sonarService.getExtractionRunById(runningRun.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (run) => {
            const index = this.runs.findIndex((r) => r.id === run.id);
            if (index !== -1) {
              this.runs[index] = run;
              this.runs = [...this.runs];
              this.cdr.markForCheck();
            }
            if (run.status !== "RUNNING") {
              this.stopPolling();
              if (run.status === "SUCCESS") {
                this.notification.success(`Extração #${run.id} concluída com sucesso. ${run.total_issues} issues extraídas.`);
              } else {
                this.notification.error(`Extração #${run.id} falhou: ${run.error_message}`);
              }
              this.sonarService.notifyExtractionCompleted();
              this.cdr.markForCheck();
            } else {
              setTimeout(poll, 5000); // continua polling normalmente
            }
          },
          error: (err) => {
            if (err.status === 504) {
              setTimeout(poll, 10000); // silencioso, tenta novamente após 10s
            } else {
              this.notification.error('Erro ao consultar status da extração. Polling interrompido.');
              this.stopPolling();
            }
          }
        });
    };

    poll();
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private buildStatusLabels(): PoTableColumnLabel[] {
    return [
      { value: "SUCCESS", color: "color-11", label: "Sucesso", textColor: "#ffffff" },
      { value: "FAILED",  color: "color-07", label: "Falhou", textColor: "#ffffff" },
      { value: "RUNNING", color: "color-01", label: "Em andamento", textColor: "#ffffff" },
    ];
  }
}
